import { Fragment, useState, useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Archive,
  ChevronDown,
  FolderKanban,
  Home,
  Inbox,
  LayoutDashboard,
  Layers,
  ListChecks,
  MoreHorizontal,
  PanelLeftClose,
  Plus,
  SlidersHorizontal,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";

import { SidebarWorkspaces } from "@/features/workspaces/SidebarWorkspaces";
import { WorkspaceCreateModal } from "@/features/workspaces/WorkspaceCreateModal";
import { useWorkspaceStore } from "@/state/workspace.store";
import { useSidebarWorkspacesUiStore } from "@/state/sidebarWorkspacesUi.store";
import { track } from "@/lib/telemetry";
import { useAuth } from "@/state/AuthContext";
import { canCreateOrgWorkspace, isPlatformAdmin } from "@/utils/access";
import { useEffectiveRole } from "@/utils/access/useEffectiveRole";
import { useUnreadNotifications } from "@/hooks/useUnreadNotifications";
import { FavoritesSidebarSection } from "@/components/shell/FavoritesSidebarSection";
import { IconTooltip } from "@/components/shell/IconTooltip";
import { CustomizeSidebarDialog } from "@/features/sidebar-layout/CustomizeSidebarDialog";
import {
  SIDEBAR_ITEM_META,
  isInline,
  type SidebarItemId,
} from "@/features/sidebar-layout/sidebarLayout";
import { useSidebarLayout } from "@/features/sidebar-layout/useSidebarLayout";
import { formatModBackslashChord } from "@/utils/modKeyLabel";
import { listPublishedDashboards } from "@/features/dashboards/api";
import { useOrgHomeState } from "@/features/organizations/useOrgHomeState";
import { useAdminWorkspacesModalStore } from "@/stores/adminWorkspacesModalStore";
import { useFeatureOn } from "@/features/feature-switches/featureSwitchSession";
import { useUIStore } from "@/stores/uiStore";

/* ────────────────────────────────────────────
   Sidebar — locked UX contract (Pass 1)
   Width is controlled by DashboardLayout shell (useResizableSidebar).
   This component must stay w-full — never reintroduce fixed w-72 here.

   ZX-TOKENS-1 nav shell (founder): bg var(--zx-color-nav) #E9F0FF ·
   border var(--zx-color-nav-border). No hard-coded #CBD5E1.
   Log out is NOT in this footer — only avatar menu + Settings → Profile.
   Popup menus are portalled to document.body and stay white.
   ──────────────────────────────────────────── */

const SIDEBAR_NAV_IDLE =
  "text-[var(--zx-color-text)] hover:bg-[var(--zx-color-surface-muted)]";
const SIDEBAR_NAV_ACTIVE =
  "bg-[var(--zx-color-surface)] text-[var(--zx-color-brand-primary)] shadow-[inset_3px_0_0_var(--zx-color-brand-primary)]";
const SIDEBAR_SECTION_LABEL =
  "text-[11px] font-extrabold uppercase tracking-wide text-[var(--zx-color-text-muted)]";
const SIDEBAR_DIVIDER = "border-[var(--zx-color-nav-border)]";

function InboxBadge() {
  const { unreadCount } = useUnreadNotifications();
  if (unreadCount === 0) return null;
  return (
    <span className="px-1.5 py-0.5 bg-red-600 text-white text-[10px] font-semibold rounded-full leading-none">
      {unreadCount > 99 ? "99+" : unreadCount}
    </span>
  );
}

/* ── Collapsible section header ── */
function SectionHeader({
  label,
  expanded,
  onToggle,
  plusLabel,
  onPlus,
  plusAlwaysVisible = false,
  onThreeDot,
  testId,
}: {
  label: string;
  expanded: boolean;
  onToggle: () => void;
  plusLabel?: string;
  onPlus?: () => void;
  plusAlwaysVisible?: boolean;
  onThreeDot?: () => void;
  testId: string;
}) {
  return (
    <div className="group/section flex items-center gap-1 px-2 py-1.5" data-testid={testId}>
      <button
        type="button"
        onClick={onToggle}
        className={`${SIDEBAR_SECTION_LABEL} flex items-center gap-1.5 hover:text-[var(--zx-color-text)] transition`}
        aria-expanded={expanded}
        data-testid={`${testId}-chevron`}
      >
        <ChevronDown
          className={`h-3.5 w-3.5 shrink-0 text-[var(--zx-color-text-muted)] transition-transform ${expanded ? "" : "-rotate-90"}`}
        />
        {label}
      </button>
      <div className="ml-auto flex items-center gap-0.5">
        {onThreeDot && (
          <button
            type="button"
            onClick={onThreeDot}
            className="rounded p-0.5 text-[var(--zx-color-text-muted)] opacity-0 group-hover/section:opacity-100 hover:bg-[var(--zx-color-surface-muted)] transition"
            aria-label={`${label} settings`}
            title={`${label} settings`}
            data-testid={`${testId}-more`}
          >
            <MoreHorizontal className="h-3.5 w-3.5" />
          </button>
        )}
        {onPlus && (
          <button
            type="button"
            onClick={onPlus}
            className={`rounded p-0.5 text-[var(--zx-color-text-muted)] hover:bg-[var(--zx-color-surface-muted)] transition ${
              plusAlwaysVisible ? "opacity-100" : "opacity-0 group-hover/section:opacity-100"
            }`}
            aria-label={plusLabel ?? `Add ${label}`}
            title={plusLabel ?? `Add ${label}`}
            data-testid={`${testId}-plus`}
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

function MenuToggleTrack({ on }: { on: boolean }) {
  return (
    <span
      className={`relative ml-auto h-5 w-9 shrink-0 rounded-full transition-colors ${
        on ? "bg-blue-600" : "bg-slate-200"
      }`}
      aria-hidden
    >
      <span
        className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-[left] ${
          on ? "left-[18px]" : "left-0.5"
        }`}
      />
    </span>
  );
}

/**
 * Workspaces section header — non-collapsible (SIDEBAR-WORKSPACES-FIXED-1).
 * Label stays always visible (ClickUp Spaces pattern). Per-workspace expand
 * lives in SidebarWorkspaces. Do not reuse SectionHeader here — Dashboards/
 * Shared collapse is a separate decision.
 */
function WorkspacesSectionHeader({
  isAdmin,
  canCreateSpace,
  navigate,
  onCreateWorkspace,
}: {
  isAdmin: boolean;
  canCreateSpace: boolean;
  navigate: (path: string) => void;
  onCreateWorkspace: () => void;
}) {
  const showArchivedWorkspaces = useSidebarWorkspacesUiStore((s) => s.showArchivedWorkspaces);
  const setShowArchivedWorkspaces = useSidebarWorkspacesUiStore((s) => s.setShowArchivedWorkspaces);

  const [moreOpen, setMoreOpen] = useState(false);
  const [moreAnchorRect, setMoreAnchorRect] = useState<DOMRect | null>(null);
  const moreWrapRef = useRef<HTMLDivElement>(null);
  const sectionMenuPanelRef = useRef<HTMLDivElement>(null);
  const ellipsisBtnRef = useRef<HTMLButtonElement>(null);
  const plusBtnRef = useRef<HTMLButtonElement>(null);
  const [ellipsisHover, setEllipsisHover] = useState(false);
  const [plusHover, setPlusHover] = useState(false);

  useEffect(() => {
    if (!moreOpen) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (moreWrapRef.current?.contains(t)) return;
      if (sectionMenuPanelRef.current?.contains(t)) return;
      setMoreOpen(false);
      setMoreAnchorRect(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [moreOpen]);

  useEffect(() => {
    if (!moreOpen) return;
    const onScroll = () => {
      setMoreOpen(false);
      setMoreAnchorRect(null);
    };
    window.addEventListener("scroll", onScroll, true);
    return () => window.removeEventListener("scroll", onScroll, true);
  }, [moreOpen]);

  useEffect(() => {
    if (!moreOpen) return;
    const onResize = () => {
      setMoreOpen(false);
      setMoreAnchorRect(null);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [moreOpen]);

  useEffect(() => {
    if (!moreOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMoreOpen(false);
        setMoreAnchorRect(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [moreOpen]);

  const closeMenu = () => {
    setMoreOpen(false);
    setMoreAnchorRect(null);
  };

  return (
    <div className="flex items-center gap-1 px-2 py-1.5" data-testid="section-workspaces">
      <span
        className={`flex items-center gap-1.5 ${SIDEBAR_SECTION_LABEL}`}
        data-testid="section-workspaces-label"
      >
        Workspaces
      </span>
      <div className="ml-auto flex items-center gap-0.5">
        <div className="relative" ref={moreWrapRef}>
          <button
            ref={ellipsisBtnRef}
            type="button"
            onClick={(e) => {
              const next = !moreOpen;
              setMoreOpen(next);
              setMoreAnchorRect(next ? e.currentTarget.getBoundingClientRect() : null);
            }}
            onMouseEnter={() => setEllipsisHover(true)}
            onMouseLeave={() => setEllipsisHover(false)}
            className={`rounded p-0.5 text-[var(--zx-color-text-muted)] transition hover:bg-[var(--zx-color-surface-muted)] ${
              moreOpen ? "bg-[var(--zx-color-surface-muted)]" : ""
            }`}
            aria-label="Workspace Settings" title="Workspace Settings"
            aria-expanded={moreOpen}
            aria-haspopup="menu"
            data-testid="section-workspaces-more"
          >
            <MoreHorizontal className="h-3.5 w-3.5" />
          </button>
          {ellipsisHover && !moreOpen && <IconTooltip label="Workspace Settings" anchorRef={ellipsisBtnRef} />}
          {moreOpen && moreAnchorRect &&
            createPortal(
              <div
                ref={sectionMenuPanelRef}
                className="fixed z-[5000] min-w-[14rem] rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
                style={{
                  top: moreAnchorRect.bottom + 4,
                  left: Math.min(
                    window.innerWidth - 224 - 8,
                    Math.max(8, moreAnchorRect.right - 224),
                  ),
                }}
                role="menu"
                aria-label="Workspace settings"
                data-testid="section-workspaces-more-menu"
              >
                {canCreateSpace && (
                  <button
                    type="button"
                    role="menuitem"
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium text-slate-800 hover:bg-slate-50"
                    onClick={() => {
                      closeMenu();
                      onCreateWorkspace();
                    }}
                    data-testid="section-workspaces-menu-create"
                  >
                    <Plus className="h-4 w-4 shrink-0 text-slate-500" aria-hidden />
                    New workspace
                  </button>
                )}
                <button
                  type="button"
                  role="menuitem"
                  className="w-full px-3 py-2 text-left text-sm font-medium text-slate-800 hover:bg-slate-50"
                  onClick={() => {
                    closeMenu();
                    if (isAdmin) {
                      useAdminWorkspacesModalStore.getState().open();
                    } else {
                      navigate("/workspaces");
                    }
                    track("sidebar.workspaces_menu", { action: "manage_workspaces" });
                  }}
                  data-testid="section-workspaces-menu-manage"
                >
                  Manage Workspaces
                </button>
                <div className="my-1 border-t border-slate-100" role="separator" />
                <button
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={showArchivedWorkspaces}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium text-slate-800 hover:bg-slate-50"
                  onClick={() => {
                    const next = !showArchivedWorkspaces;
                    setShowArchivedWorkspaces(next);
                    track("sidebar.workspaces_plus_menu", {
                      action: "toggle_show_archived_workspaces",
                      enabled: next,
                    });
                  }}
                  data-testid="section-workspaces-menu-show-archived"
                >
                  <Archive className="h-4 w-4 shrink-0 text-slate-500" aria-hidden />
                  <span className="min-w-0 flex-1">Show archived</span>
                  <MenuToggleTrack on={showArchivedWorkspaces} />
                </button>
              </div>,
              document.body,
            )}
        </div>
        {canCreateSpace && (
          <>
            <button
              ref={plusBtnRef}
              type="button"
              onClick={() => {
                setMoreOpen(false);
                onCreateWorkspace();
              }}
              onMouseEnter={() => setPlusHover(true)}
              onMouseLeave={() => setPlusHover(false)}
              className="rounded p-0.5 text-[var(--zx-color-text-muted)] transition hover:bg-[var(--zx-color-surface-muted)]"
              aria-label="Create Workspace" title="Create Workspace"
              data-testid="section-workspaces-plus"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
            {plusHover && <IconTooltip label="Create Workspace" anchorRef={plusBtnRef} />}
          </>
        )}
      </div>
    </div>
  );
}

export function Sidebar() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isHomeNavActive = location.pathname === "/home";
  const setSidebarOpen = useUIStore((s) => s.setSidebarOpen);
  const { activeWorkspaceId, setActiveWorkspace, clearActiveWorkspace } = useWorkspaceStore();
  const { workspaceCount, isLoading: orgWorkspaceLoading } = useOrgHomeState();
  const { can, is } = useEffectiveRole();
  const isAdmin = isPlatformAdmin(user);
  const canCreateSpace = canCreateOrgWorkspace(user);
  const programsPortfoliosEnabled = useFeatureOn("portfolios");
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const [closeHover, setCloseHover] = useState(false);
  const organizationId =
    typeof user?.organizationId === "string" ? user.organizationId : null;
  /** Permanent Dashboards nav — capability only; not record-count or workspace gated. */
  const canViewDashboards = can("dashboard.view.published");

  useEffect(() => {
    if (orgWorkspaceLoading) return;
    if (workspaceCount === 0 && activeWorkspaceId) {
      clearActiveWorkspace();
    }
  }, [orgWorkspaceLoading, workspaceCount, activeWorkspaceId, clearActiveWorkspace]);

  // Phase 4.7.1: hasProjects check + standalone Projects nav link removed.
  // Projects are surfaced through SidebarWorkspaces tree expansion.

  // Published dashboards = "Shared" content (only real sharing model that exists)
  const { data: publishedDashboards, isFetched: sharedFetched } = useQuery({
    queryKey: ["published-dashboards", organizationId, activeWorkspaceId],
    queryFn: () => listPublishedDashboards(activeWorkspaceId!),
    enabled: !!organizationId && !!activeWorkspaceId,
    staleTime: 30_000,
  });
  const hasSharedItems =
    sharedFetched && (publishedDashboards?.length ?? 0) > 0;

  // Section collapse state (Workspaces is intentionally non-collapsible)
  const [favoritesOpen, setFavoritesOpen] = useState(true);
  const [sharedOpen, setSharedOpen] = useState(true);

  // Create workspace modal
  const [showCreateModal, setShowCreateModal] = useState(false);

  const handleCreateWorkspace = () => {
    setShowCreateModal(true);
    track("sidebar.create_workspace", {});
  };

  const handleWorkspaceCreated = (workspaceId: string) => {
    setActiveWorkspace(workspaceId);
    track("workspace.created", { workspaceId });
    setShowCreateModal(false);
  };

  // ── Customize sidebar ────────────────────────────────────────────────────
  // The saved layout decides which rows appear and in what order. It can hide a
  // row, never reveal one: every row is still gated by role, capability or
  // feature switch here (and by the server for the page itself).
  const layout = useSidebarLayout();
  const [customizeOpen, setCustomizeOpen] = useState(false);

  const canUse = (id: SidebarItemId): boolean => {
    switch (id) {
      case "inbox":
        // `inbox.view` false for Platform VIEWER (taxonomy §3.13 / §5.3)
        return can("inbox.view");
      case "mywork":
        // Org queue; paid platform roles only (not taxonomy `task.view` alone)
        return is("paid");
      case "dashboards":
        return canViewDashboards;
      case "shared":
        // Data-driven: only when published dashboards exist.
        return hasSharedItems;
      case "portfolios_programs":
        return programsPortfoliosEnabled;
      default:
        return true;
    }
  };

  /** Positioned rows only; inline items render inside another row. */
  const rows = layout.order.filter((id) => !isInline(id) && canUse(id));
  const showPortfoliosPrograms =
    layout.order.includes("portfolios_programs") && programsPortfoliosEnabled;

  const renderRow = (id: SidebarItemId): ReactNode => {
    switch (id) {
      case "inbox":
        return (
          <NavLink
            data-testid="nav-inbox"
            data-sb-row
            to={{ pathname: "/inbox", search: "" }}
            className={({ isActive }) =>
              `flex items-center justify-between rounded-lg px-3 py-2 text-sm font-bold tracking-tight transition ${
                isActive ? SIDEBAR_NAV_ACTIVE : SIDEBAR_NAV_IDLE
              }`
            }
          >
            <span className="flex items-center gap-2">
              <Inbox className="h-4 w-4 shrink-0" />
              Inbox
            </span>
            <InboxBadge />
          </NavLink>
        );
      case "mywork":
        return (
          <NavLink
            data-testid="nav-my-work"
            data-sb-row
            to="/my-work"
            className={({ isActive }) =>
              `flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-bold tracking-tight transition ${
                isActive ? SIDEBAR_NAV_ACTIVE : SIDEBAR_NAV_IDLE
              }`
            }
          >
            <ListChecks className="h-4 w-4 shrink-0" />
            My Work
          </NavLink>
        );
      case "favorites":
        // Permanent platform section (empty/loading/error stay mounted).
        return (
          <FavoritesSidebarSection
            expanded={favoritesOpen}
            onToggleExpanded={() => setFavoritesOpen(!favoritesOpen)}
          />
        );
      case "workspaces":
        // Fixed header …/+ ; child rows always visible.
        // Org → Workspace → Portfolio → Program → Project.
        return (
          <div className="group/workspace-shell">
            <WorkspacesSectionHeader
              isAdmin={isAdmin}
              canCreateSpace={canCreateSpace}
              navigate={navigate}
              onCreateWorkspace={handleCreateWorkspace}
            />
            <div className="space-y-1">
              {orgWorkspaceLoading ? (
                <div
                  className="px-2 py-1.5 text-xs text-[var(--zx-color-text-muted)]"
                  data-testid="workspaces-empty-loading"
                >
                  Loading workspace info…
                </div>
              ) : workspaceCount === 0 ? (
                /* Empty Workspaces: no picker row until at least one workspace exists */
                <div className="px-2 py-1" data-testid="workspaces-empty-first">
                  {canCreateSpace ? (
                    <button
                      type="button"
                      onClick={handleCreateWorkspace}
                      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm font-medium text-[var(--zx-color-text-muted)] transition hover:bg-[var(--zx-color-surface-muted)] hover:text-[var(--zx-color-text)]"
                      data-testid="empty-create-workspace"
                    >
                      <Plus className="h-3.5 w-3.5 shrink-0 text-[var(--zx-color-text-muted)]" aria-hidden />
                      New workspace
                    </button>
                  ) : (
                    <p className="px-2 py-1.5 text-xs text-[var(--zx-color-text-muted)]">
                      Ask an organization admin to create the first workspace.
                    </p>
                  )}
                </div>
              ) : (
                /*
                 * Phase 4.7.1 hotfix: standalone "Projects" nav link removed.
                 * Projects are now reachable through workspace tree expansion
                 * in SidebarWorkspaces (chevron → child project list), so the
                 * duplicate top-level entry was redundant.
                 */
                <div className="px-1">
                  <SidebarWorkspaces />
                </div>
              )}

              {/* Workspace-scoped surfaces — inside workspace context only */}
              {activeWorkspaceId && showPortfoliosPrograms ? (
                <div className="mt-1 space-y-0.5 px-1" data-testid="sidebar-workspace-scoped-nav">
                  <NavLink
                    data-testid="nav-portfolios"
                    data-sb-row
                    to={`/workspaces/${activeWorkspaceId}/portfolios`}
                    className={({ isActive }) =>
                      `flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium tracking-tight transition ${
                        isActive ? SIDEBAR_NAV_ACTIVE : SIDEBAR_NAV_IDLE
                      }`
                    }
                  >
                    <Layers className="h-4 w-4 shrink-0" />
                    Portfolios
                  </NavLink>
                  <NavLink
                    data-testid="nav-programs"
                    data-sb-row
                    to={`/workspaces/${activeWorkspaceId}/programs`}
                    className={({ isActive }) =>
                      `flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium tracking-tight transition ${
                        isActive ? SIDEBAR_NAV_ACTIVE : SIDEBAR_NAV_IDLE
                      }`
                    }
                  >
                    <FolderKanban className="h-4 w-4 shrink-0" />
                    Programs
                  </NavLink>
                  {/* /risks sidebar entry removed — RisksModule has zero controllers (DEAD-UI-SWEEP). Route kept for remount. */}
                </div>
              ) : null}
            </div>
          </div>
        );
      case "dashboards":
        // Permanent for dashboard.view.published (any workspace / count / API state).
        return (
          <NavLink
            data-testid="nav-dashboards"
            data-sb-row
            to="/dashboards"
            aria-label="Dashboards"
            className={({ isActive }) =>
              `flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-bold tracking-tight transition ${
                isActive ? SIDEBAR_NAV_ACTIVE : SIDEBAR_NAV_IDLE
              }`
            }
          >
            <LayoutDashboard className="h-4 w-4 shrink-0" aria-hidden />
            Dashboards
          </NavLink>
        );
      case "shared":
        // Published dashboards; visible when items exist.
        // TODO: shared surface capability token TBD (role-taxonomy-mvp.md); gate is data-driven only for now.
        return (
          <>
            <SectionHeader
              label="Shared"
              expanded={sharedOpen}
              onToggle={() => setSharedOpen(!sharedOpen)}
              testId="section-shared"
            />
            {sharedOpen && (
              <div className="ml-2 space-y-0.5" data-testid="shared-list">
                {publishedDashboards!.map((d) => (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => navigate(`/dashboards/${d.id}`)}
                    className="block w-full truncate rounded-lg px-3 py-1.5 text-left text-sm font-medium text-[var(--zx-color-text)] transition hover:bg-[var(--zx-color-surface-muted)]"
                    data-testid={`shared-item-${d.id}`}
                  >
                    <span className="mr-1.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--zx-color-text-muted)]">
                      Dashboard
                    </span>
                    {d.name}
                  </button>
                ))}
              </div>
            )}
          </>
        );
      default:
        return null;
    }
  };

  return (
    <aside
      className={`relative z-40 flex h-full w-full flex-col border-r border-[var(--zx-color-nav-border)] bg-[var(--zx-color-nav)] ${
        // Compact trims the vertical padding of every row tagged data-sb-row.
        layout.density === "compact" ? "[&_[data-sb-row]]:py-1" : ""
      }`}
      data-testid="sidebar"
      data-density={layout.density}
    >
      {/* Platform brand → Inbox; collapse control top-right (SIDEBAR-ADD-CLOSE-BUTTON-1) */}
      <div className={`flex items-center gap-1 border-b ${SIDEBAR_DIVIDER} p-3`}>
        <NavLink
          to={{ pathname: "/inbox", search: "" }}
          className="flex min-w-0 flex-1 items-center gap-2.5 rounded-xl px-2 py-1.5 transition hover:bg-[var(--zx-color-surface-muted)]"
          data-testid="platform-brand"
          aria-label="Zephix home"
        >
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-blue-700 to-teal-500 text-xs font-bold text-white shadow-sm">
            Z
          </div>
          <span className="text-sm font-semibold tracking-tight text-[var(--zx-color-text)]">
            Zephix
          </span>
        </NavLink>
        <button
          ref={closeBtnRef}
          type="button"
          data-testid="sidebar-close-btn"
          aria-label="Close sidebar" title="Close sidebar"
          onClick={() => setSidebarOpen(false)}
          onMouseEnter={() => setCloseHover(true)}
          onMouseLeave={() => setCloseHover(false)}
          onFocus={() => setCloseHover(true)}
          onBlur={() => setCloseHover(false)}
          className="shrink-0 rounded-lg p-1.5 text-[var(--zx-color-text)] transition hover:bg-[var(--zx-color-surface-muted)] hover:text-[var(--zx-color-text)]"
        >
          <PanelLeftClose className="h-4 w-4" aria-hidden />
        </button>
        {closeHover && (
          <IconTooltip
            testId="sidebar-close-tooltip"
            anchorRef={closeBtnRef}
            label={
              <span className="flex min-w-[9rem] items-center justify-between gap-4">
                <span>Close sidebar</span>
                {/* Chord bound on SidebarResizeHandle: metaKey || ctrlKey */}
                <span className="font-normal text-slate-400" data-testid="sidebar-close-chord">
                  {formatModBackslashChord()}
                </span>
              </span>
            }
          />
        )}
      </div>

      <nav className="flex-1 overflow-y-auto px-2 py-2 space-y-0.5">
        {/* ── Home — /home: Admin Home for ADMIN, Inbox for MEMBER / VIEWER (HomeRoute). ── */}
        <Link
          data-testid="nav-home"
          data-sb-row
          to="/home"
          aria-current={isHomeNavActive ? "page" : undefined}
          className={`mb-2 flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-bold tracking-tight transition ${
            isHomeNavActive ? SIDEBAR_NAV_ACTIVE : SIDEBAR_NAV_IDLE
          }`}
        >
          <Home className="h-4 w-4 shrink-0" />
          Home
        </Link>

        {/* Rows render in the user's saved order. A divider sits above every
            section and below one; consecutive links stay together. */}
        {rows.map((id, index) => {
          const previous = index > 0 ? SIDEBAR_ITEM_META[rows[index - 1]] : null;
          const needsDivider =
            previous !== null &&
            (SIDEBAR_ITEM_META[id].kind === "section" || previous.kind === "section");
          return (
            <Fragment key={id}>
              {needsDivider ? <div className={`my-2 border-t ${SIDEBAR_DIVIDER}`} /> : null}
              {renderRow(id)}
            </Fragment>
          );
        })}
      </nav>

      {/* Footer — Customize only. Log out lives in avatar menu + Settings → Profile. */}
      <div
        className={`space-y-0.5 border-t ${SIDEBAR_DIVIDER} px-2 pt-2 pb-[max(1.25rem,env(safe-area-inset-bottom))]`}
        data-testid="sidebar-footer"
      >
        <button
          type="button"
          data-testid="sidebar-customize"
          data-sb-row
          onClick={() => setCustomizeOpen(true)}
          className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium tracking-tight transition ${SIDEBAR_NAV_IDLE}`}
        >
          <SlidersHorizontal className="h-4 w-4 shrink-0" aria-hidden />
          Customize sidebar
        </button>
      </div>

      <WorkspaceCreateModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={handleWorkspaceCreated}
      />

      {/* Mounted only while open so closed sidebars carry no mutation hooks. */}
      {customizeOpen ? (
        <CustomizeSidebarDialog
          isOpen
          onClose={() => setCustomizeOpen(false)}
          layout={layout}
          // "Shared" is listed even while empty: it appears once dashboards exist.
          canUse={(id) => (id === "shared" ? true : canUse(id))}
        />
      ) : null}
    </aside>
  );
}

